/* eslint-disable @typescript-eslint/no-explicit-any */
import { Close, Warning, Edit } from "@mui/icons-material";
import {
    Chip,
    Modal,
    IconButton,
    Popover,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ptBR } from "@mui/x-date-pickers/locales";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";

import ConfirmModalDefault from "../../components/Modal/ConfirmModalDefault";
import Toast from "../../components/Toast";
import { getApiErrorMessage } from "../../helpers/api/getApiErrorMessage";
import { formatCurrencyToShow } from "../../helpers/currency";
import {
    isValidDueDate,
    MIN_DUE_DATE_MESSAGE,
} from "../../helpers/invoice/invoiceValidation";
import { INVOICE_PAYMENT_METHOD_OPTIONS } from "../../helpers/invoicePaymentMethods";
import { getStatusChip } from "../../helpers/statusChip/statusChip";
import { defaultToast } from "../../helpers/toast/defaultToast";
import { TransformDateEUA } from "../../helpers/tranformDateEUA";
import { api } from "../../services/api";
import { Invoice } from "./MonthlyBilling/Invoice";
import { BillingDetailsModalStyled } from "./styles";

interface Props {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handleCloseBillingDetailsModal: () => void;
    openBillingDetailsModal: boolean;
    currentBillingDetails: BillingsDetailsProps | null;
    handleClick: () => void;
    holder?: any;
    getFinancial?: () => void;
    setOpenBillingDetailsModal: (data: boolean) => void;
}

function BillingDetailsModal({
    handleCloseBillingDetailsModal,
    openBillingDetailsModal,
    currentBillingDetails,
    handleClick,
    holder,
    getFinancial,
    setOpenBillingDetailsModal,
}: Props) {
    const [loadingDueDate, setLoadingDueDate] = useState<boolean>(false);
    const [openToast, setOpenToast] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [showConfirmUpdateDueDate, setShowConfirmUpdateDueDate] =
        useState(false);
    const [invoicePaymentMethods, setInvoicePaymentMethods] = useState("");
    const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] =
        useState(false);

    const [dateSelected, setDateSelected] = useState<Dayjs | null>(null);

    useEffect(() => {
        setInvoicePaymentMethods(
            currentBillingDetails?.invoice_payment_methods ?? ""
        );
    }, [currentBillingDetails]);

    const handleOpenEditDate = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseEditDate = () => {
        setAnchorEl(null);
        setDateSelected(null);
    };

    const handleCloseEditDatePopover = () => {
        setAnchorEl(null);
    };

    function handleCloseToast() {
        setOpenToast(false);
    }

    const updated = () => {
        handleClick();
    };

    const handleOpenConfirmUpdateDueDay = () => {
        if (!dateSelected) {
            defaultToast("warning", "Escolha uma data diferente!");
            return;
        }

        if (!isValidDueDate(dateSelected)) {
            defaultToast("warning", MIN_DUE_DATE_MESSAGE);
            return;
        }

        handleCloseEditDatePopover();
        setShowConfirmUpdateDueDate(true);
    };

    const handleUpdateDueDay = async () => {
        if (!dateSelected) {
            defaultToast("warning", "Escolha uma data diferente!");
            return;
        }

        if (!isValidDueDate(dateSelected)) {
            defaultToast("warning", MIN_DUE_DATE_MESSAGE);
            setShowConfirmUpdateDueDate(false);
            return;
        }

        try {
            setLoadingDueDate(true);

            const dateFormated = TransformDateEUA(
                new Date(dateSelected as any).toLocaleDateString()
            )?.split(" ")[0];

            const responseUpdateDueDay = await api.put(
                `monthly-billing/${currentBillingDetails?.id}`,
                {
                    due_date: dateFormated,
                }
            );

            if (responseUpdateDueDay.status) {
                defaultToast(
                    "success",
                    "Data de vencimento alterada com sucesso!"
                );
                handleCloseBillingDetailsModal();
                setTimeout(() => {
                    window.location.reload();
                }, 1500);
            }
        } catch (error) {
            defaultToast(
                "error",
                getApiErrorMessage(
                    error,
                    "Não foi possível alterar a data de vencimento."
                )
            );
        } finally {
            setLoadingDueDate(false);
        }
    };

    const handleUpdateInvoicePaymentMethods = async (
        event: SelectChangeEvent
    ) => {
        if (!currentBillingDetails?.id) {
            return;
        }

        const nextValue = event.target.value;
        setInvoicePaymentMethods(nextValue);

        try {
            setIsLoadingPaymentMethods(true);
            await api.put(`monthly-billing/${currentBillingDetails.id}`, {
                invoice_payment_methods: nextValue || null,
            });
            defaultToast("success", "Tipo de pagamento da fatura atualizado");
            getFinancial?.();
        } catch (error) {
            defaultToast(
                "error",
                getApiErrorMessage(
                    error,
                    "Não foi possível atualizar o tipo de pagamento."
                )
            );
            setInvoicePaymentMethods(
                currentBillingDetails.invoice_payment_methods ?? ""
            );
        } finally {
            setIsLoadingPaymentMethods(false);
        }
    };

    const open = Boolean(anchorEl);
    const id = open ? "simple-popover" : undefined;

    return (
        <Modal
            open={openBillingDetailsModal}
            onClose={handleCloseBillingDetailsModal}
        >
            <BillingDetailsModalStyled>
                <div className="topModalContainer">
                    <h3>Detalhes da fatura</h3>
                    {/* eslint-disable-next-line */}
                    <button
                        onClick={handleCloseBillingDetailsModal}
                        type="button"
                    >
                        <Close />
                    </button>
                </div>

                <div className="mainContainerBilling">
                    <div className="titleModal" />
                    <div className="rowsContainer">
                        <div className="keyAndValueContainer">
                            <p className="keyContent">Situação:</p>
                            <p className="valueContent">
                                {currentBillingDetails
                                    ? getStatusChip(
                                          currentBillingDetails.status_id
                                      )
                                    : getStatusChip("" as any)}
                            </p>
                        </div>
                        <div className="keyAndValueContainer">
                            <p className="keyContent">Valor total:</p>
                            <p className="valueContent">
                                {" "}
                                R$:{" "}
                                {currentBillingDetails &&
                                    formatCurrencyToShow(
                                        (currentBillingDetails &&
                                            currentBillingDetails?.total_amount) ||
                                            ""
                                    )}
                            </p>
                        </div>
                    </div>
                    <div className="rowsContainer">
                        <div className="keyAndValueContainer">
                            <p className="keyContent">Data de referência:</p>
                            <p className="valueContent">
                                {(currentBillingDetails &&
                                    currentBillingDetails?.date_reference
                                        .split("-")
                                        .reverse()
                                        .join("/")) ||
                                    ""}
                            </p>
                        </div>
                        <div className="keyAndValueContainer">
                            <p className="keyContent">Vencimento em:</p>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                <p className="valueContent">
                                    {(currentBillingDetails &&
                                        currentBillingDetails?.due_date
                                            .slice(0, -9)
                                            .split("-")
                                            .reverse()
                                            .join("/")) ||
                                        ""}
                                </p>
                                <IconButton
                                    title="Editar vencimento"
                                    onClick={handleOpenEditDate}
                                >
                                    <Edit color="primary" />
                                </IconButton>

                                <Popover
                                    id={id}
                                    open={open}
                                    anchorEl={anchorEl}
                                    onClose={handleCloseEditDate}
                                    anchorOrigin={{
                                        vertical: "bottom",
                                        horizontal: "left",
                                    }}
                                >
                                    <div className="content-date-picker-modal">
                                        <LocalizationProvider
                                            dateAdapter={AdapterDayjs}
                                            localeText={
                                                ptBR.components
                                                    .MuiLocalizationProvider
                                                    .defaultProps.localeText
                                            }
                                            adapterLocale="pt-br"
                                        >
                                            <StaticDatePicker
                                                defaultValue={dayjs(
                                                    currentBillingDetails?.due_date?.split(
                                                        " "
                                                    )[0]
                                                )}
                                                minDate={dayjs().startOf("day")}
                                                disablePast
                                                onChange={(dateSelected) => {
                                                    setDateSelected(
                                                        dateSelected
                                                    );
                                                }}
                                                onAccept={
                                                    handleOpenConfirmUpdateDueDay
                                                }
                                                onClose={
                                                    handleCloseEditDatePopover
                                                }
                                                loading={loadingDueDate}
                                            />
                                        </LocalizationProvider>
                                    </div>
                                </Popover>
                            </div>
                        </div>
                    </div>
                    <div className="rowsContainer">
                        <div className="keyAndValueContainer">
                            <p className="keyContent">Data de fechamento:</p>
                            <p className="valueContent">
                                {(currentBillingDetails &&
                                    currentBillingDetails?.closing_date
                                        .slice(0, -9)
                                        .split("-")
                                        .reverse()
                                        .join("/")) ||
                                    ""}
                            </p>
                        </div>
                        <div className="keyAndValueContainer">
                            <p className="keyContent">Id da mensalidade:</p>
                            <p className="valueContent">
                                {(currentBillingDetails &&
                                    currentBillingDetails?.uuid) ||
                                    ""}
                            </p>
                        </div>
                        <div className="keyAndValueContainer">
                            <p className="keyContent">Tipo de pagamento:</p>
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                                <InputLabel id="billing-payment-methods-label">
                                    Tipo de pagamento
                                </InputLabel>
                                <Select
                                    labelId="billing-payment-methods-label"
                                    label="Tipo de pagamento"
                                    value={invoicePaymentMethods}
                                    disabled={isLoadingPaymentMethods}
                                    onChange={handleUpdateInvoicePaymentMethods}
                                >
                                    <MenuItem value="">
                                        Padrão (cliente/config)
                                    </MenuItem>
                                    {INVOICE_PAYMENT_METHOD_OPTIONS.map(
                                        (option) => (
                                            <MenuItem
                                                key={option.value}
                                                value={option.value}
                                            >
                                                {option.label}
                                            </MenuItem>
                                        )
                                    )}
                                </Select>
                            </FormControl>
                        </div>

                        {(() => {
                            const statusId = currentBillingDetails?.status_id;
                            const canShowPaymentLink =
                                statusId === 1 ||
                                statusId === 2 ||
                                statusId === 3;
                            const invoiceAmount = Number(
                                currentBillingDetails?.total_amount ?? 0
                            );
                            const paymentReference =
                                currentBillingDetails?.payment
                                    ?.payment_reference;

                            if (!canShowPaymentLink) {
                                return null;
                            }

                            return (
                                <div className="rowsContainer">
                                    <div className="payment-link">
                                        {invoiceAmount <= 0 ||
                                        !paymentReference ? (
                                            <span>
                                                <Chip
                                                    label={
                                                        invoiceAmount <= 0
                                                            ? "Faturas vazias não podem ser pagas"
                                                            : "Link de pagamento indisponível"
                                                    }
                                                    icon={<Warning />}
                                                    color="warning"
                                                    size="medium"
                                                />
                                            </span>
                                        ) : (
                                            <h4>
                                                Link de pagamento:{" "}
                                                <a
                                                    href={`${window.env.REACT_APP_LINK_PAYMENT}checkout?id=${paymentReference}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    abrir link.
                                                </a>
                                            </h4>
                                        )}
                                    </div>
                                </div>
                            );
                        })()}
                    </div>

                    {currentBillingDetails &&
                        currentBillingDetails?.payment?.payment_date && (
                            <div className="rowsContainer">
                                <div className="keyAndValueContainer">
                                    <p className="keyContent">
                                        Data do pagamento:
                                    </p>
                                    <p className="valueContent">
                                        {new Date(
                                            currentBillingDetails?.payment.payment_date
                                        ).toLocaleDateString()}
                                    </p>
                                </div>
                            </div>
                        )}

                    <div>
                        <Invoice
                            setOpenBillingDetailsModal={
                                setOpenBillingDetailsModal
                            }
                            getFinancial={getFinancial as any}
                            billing={
                                currentBillingDetails?.billing_products ?? []
                            }
                            monthlyBilling={currentBillingDetails}
                            updated={updated}
                            holder={holder}
                        />
                    </div>
                </div>
                <Toast
                    open={openToast}
                    onClose={() => handleCloseToast()}
                    // severity={toastRequisitionResult}
                    // text={textToast}
                />
                <ConfirmModalDefault
                    loading={loadingDueDate}
                    onClick={() => {
                        handleUpdateDueDay();
                    }}
                    isOpen={showConfirmUpdateDueDate}
                    handleClose={() => {
                        setShowConfirmUpdateDueDate(false);
                    }}
                    buttonColor="success"
                    title="Deseja realmente atualizar a data de vencimento?"
                />
            </BillingDetailsModalStyled>
        </Modal>
    );
}
export default BillingDetailsModal;
